import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * AuthCallback - Handles OAuth callback from Emergent Auth
 * Processes session_id from URL fragment and exchanges for session token
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const { processGoogleSession } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use useRef to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      // Extract session_id from URL fragment
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        console.error('No session_id found in URL');
        navigate('/auth/login', { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];
      
      try {
        const result = await processGoogleSession(sessionId);
        
        if (result.success) {
          // Clear the hash and redirect to dashboard
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/juswork/prolab/dashboard', { 
            replace: true,
            state: { user: result.user }
          });
        } else {
          console.error('Google auth failed:', result.error);
          navigate('/auth/login', { 
            replace: true,
            state: { error: result.error }
          });
        }
      } catch (error) {
        console.error('Session processing error:', error);
        navigate('/auth/login', { replace: true });
      }
    };

    processSession();
  }, [processGoogleSession, navigate]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A] mx-auto mb-4" />
        <p className="text-[#1E3A8A]">Completing sign in...</p>
      </div>
    </div>
  );
}
